import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { FC } from "react";
import styled from "styled-components";
import { LegacyImage } from "components";
import { LinkedinLogo } from "icons";
import { Carousel5EmployeeContent } from "types/graphql/generated";

interface Props {
  className?: string;
  content: Carousel5EmployeeContent;
}

const Employee: FC<Props> = withErrorBoundary(
  ({
    className = "",
    content: {
      name,
      role,
      link,
      photo: {
        data: { photo },
      },
    },
  }) => {
    return (
      <StyledEmployee className={className}>
        <div className="Employee__innerAspectRatioContainer">
          <div className="Carousel5__nameContainer">
            <p className="Font_18_26_700">{name}</p>
            <p className="Carousel5__role Font_14_20_450">{role}</p>
          </div>
          <div className="Carousel5__background" />
          <a href={link} target="_blank" rel="noopener noreferrer" aria-label={photo.alt} className="Carousel5__link">
            <LinkedinLogo className="Carousel5__linkedinLogo" />
          </a>
          <LegacyImage
            {...photo}
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            className="Carousel5__image"
          />
        </div>
      </StyledEmployee>
    );
  },
  {}
);

const StyledEmployee = styled.div`
  position: relative;
  padding-top: 120.2%;

  .Employee__innerAspectRatioContainer {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: ${({ theme }) => theme.colors.fog["00"]};
    padding: 12px;
    border-radius: 8px;
    overflow: hidden;
  }

  .Employee__innerAspectRatioContainer:hover {
    .Carousel5__background {
      opacity: 0.7;
    }

    .Carousel5__link {
      opacity: 1;
      transition: 0.175s ease-in-out;
    }
  }

  .Carousel5__nameContainer {
    position: relative;
    display: flex;
    flex-direction: column;
    z-index: 1;
  }

  .Carousel5__role {
    margin-top: 4px;
  }

  .Carousel5__image {
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    bottom: 0;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  .Carousel5__background {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: block;
    background: #000;
    border-radius: 8px;
    opacity: 0;
    transition: 0.175s ease-in-out;
    z-index: 1;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  .Carousel5__link {
    position: absolute;
    width: 50px;
    height: 50px;
    right: 16px;
    bottom: 16px;
    line-height: 0;
    opacity: 0;
    border-radius: 50%;
    overflow: hidden;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Employee__innerAspectRatioContainer {
      padding: 8px 12px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .Employee__innerAspectRatioContainer {
      padding: 12px;
    }

    .Carousel5__background {
      display: none;
    }

    .Carousel5__link {
      opacity: 1;
    }
  }
`;

gql`
  fragment Carousel5EmployeeContent on ComponentNestedCarousel5Team {
    id
    name
    role
    link
    photo {
      data {
        photo: attributes {
          src: hash
          alt: alternativeText
        }
      }
    }
  }
`;

export { Employee };
